import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import PageHeader from '../../../components/PageHeader';
import KPICard from '../../../components/KPICard';
import { getSecFilingInsights } from '../../../api';
import useSecFilingBootstrap from '../hooks/useSecFilingBootstrap';
import { assignFilterArray } from '../../../utils/filtersApi';
import SecFilingFiltersDrawer from '../components/SecFilingFiltersDrawer';
import SecFilingInsightsSummary from '../components/SecFilingInsightsSummary';
import useSecFilingFilters from '../hooks/useSecFilingFilters';
import { FALLBACK_SEC_PERIODS, resolveSecPeriods } from '../utils/secFilingConstants';
import { SEC_FILTER_DEFAULTS } from '../utils/secFilingFilters';

const KPI_VALUE_DELAY_BASE_MS = 220;
const KPI_STAGGER_MS = 65;
const KPI_COUNT_UP_DURATION_MS = 680;

export default function SecFilingTracker() {
  const [activePeriodKey, setActivePeriodKey] = useState('1D');
  const [animateKpiNumbers, setAnimateKpiNumbers] = useState(true);
  const [tablePage, setTablePage] = useState(1);
  const [tableCompanyFilter, setTableCompanyFilter] = useState(null);
  const { meta, filterOptions } = useSecFilingBootstrap();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insightsEntered, setInsightsEntered] = useState(false);
  const insightsFetchIdRef = useRef(0);
  const hasInsightsRef = useRef(false);
  const periodSwitchPendingRef = useRef(null);
  const [periodSwitchVersion, setPeriodSwitchVersion] = useState(0);

  const filters = useSecFilingFilters({ filterOptions });
  const {
    hasFiltersApplied,
    handleClearFilters,
    showTableFilters,
    setShowTableFilters,
    setFilterCompany,
    filterCompany,
    filterFormType,
    filterXbrlTag,
    filedStart,
    filedEnd,
  } = filters;

  const periods = useMemo(() => resolveSecPeriods(meta), [meta]);

  useEffect(() => {
    const lastCardDelay = KPI_VALUE_DELAY_BASE_MS + (periods.length - 1) * KPI_STAGGER_MS;
    const timeoutId = window.setTimeout(
      () => setAnimateKpiNumbers(false),
      lastCardDelay + KPI_COUNT_UP_DURATION_MS + 50,
    );
    return () => window.clearTimeout(timeoutId);
  }, []);

  const activePeriod = useMemo(
    () => periods.find((period) => period.key === activePeriodKey) || periods[0] || FALLBACK_SEC_PERIODS[0],
    [activePeriodKey, periods],
  );

  useEffect(() => {
    let cancelled = false;
    const fetchInsights = async () => {
      if (!filterOptions) return;
      const fetchId = insightsFetchIdRef.current + 1;
      insightsFetchIdRef.current = fetchId;
      if (!hasInsightsRef.current) setLoading(true);
      try {
        const body = { periodKey: activePeriod.key };
        if (tableCompanyFilter) {
          body.companyFilter = tableCompanyFilter;
        }
        assignFilterArray(body, 'company', filterCompany, SEC_FILTER_DEFAULTS.company, filterOptions.companies);
        assignFilterArray(body, 'filingType', filterFormType, SEC_FILTER_DEFAULTS.type, filterOptions.formTypes);
        assignFilterArray(body, 'xbrlTag', filterXbrlTag, SEC_FILTER_DEFAULTS.xbrlTag, filterOptions.xbrlTags);
        if (filedStart) body.filedFrom = format(filedStart, 'yyyy-MM-dd');
        if (filedEnd) body.filedTo = format(filedEnd, 'yyyy-MM-dd');

        const response = await getSecFilingInsights(body);
        if (cancelled || fetchId !== insightsFetchIdRef.current) return;
        setInsights(response.data?.data || null);
        hasInsightsRef.current = true;
        if (
          periodSwitchPendingRef.current
          && periodSwitchPendingRef.current === activePeriod.key
        ) {
          setPeriodSwitchVersion((version) => version + 1);
          periodSwitchPendingRef.current = null;
        }
      } catch (error) {
        console.error('Error fetching SEC insights:', error);
        if (!cancelled && fetchId === insightsFetchIdRef.current) setInsights(null);
      } finally {
        if (!cancelled && fetchId === insightsFetchIdRef.current) setLoading(false);
      }
    };

    const timer = setTimeout(fetchInsights, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    activePeriod.key,
    filterOptions,
    filterCompany,
    filterFormType,
    filterXbrlTag,
    filedStart,
    filedEnd,
    tableCompanyFilter,
  ]);

  const selectPeriod = useCallback((periodKey) => {
    const period = periods.find((item) => item.key === periodKey);
    if (!period || period.key === activePeriodKey) return;
    setTableCompanyFilter(null);
    setTablePage(1);
    periodSwitchPendingRef.current = period.key;
    setActivePeriodKey(period.key);
  }, [activePeriodKey, periods]);

  const handleClearAllFilters = useCallback(() => {
    handleClearFilters();
    setTableCompanyFilter(null);
    setTablePage(1);
  }, [handleClearFilters]);

  useEffect(() => {
    if (!insights || loading || insightsEntered) return undefined;
    const timer = window.setTimeout(() => setInsightsEntered(true), 1100);
    return () => window.clearTimeout(timer);
  }, [insights, loading, insightsEntered]);

  const periodTotals = meta?.periodTotals || {};
  const periodSubtitles = meta?.periodSubtitles || {};

  return (
    <>
      <PageHeader
        title="SEC Filing"
        activePageTab="Insights"
        hasFiltersApplied={hasFiltersApplied}
        onClearFilters={handleClearAllFilters}
        showTableFilters={showTableFilters}
        onToggleFilters={() => setShowTableFilters((open) => !open)}
      />
      <div className="body">
        <div
          className={[
            'table-area',
            'sec-filing-panel',
            'insights-panel',
            insightsEntered ? 'sec-filing-insights--entered' : '',
          ].filter(Boolean).join(' ')}
        >
          <div className="kpi-banner-grid sec-filing-period-grid">
            {periods.map((period, index) => {
              const isActive = period.key === activePeriod.key;
              return (
                <div
                  key={period.key}
                  role="button"
                  tabIndex={0}
                  className={`sec-filing-period-card${isActive ? ' is-active' : ''}`}
                  onClick={() => selectPeriod(period.key)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      selectPeriod(period.key);
                    }
                  }}
                >
                  <KPICard
                    title={period.key}
                    value={periodTotals[period.key] ?? 0}
                    subtitle={periodSubtitles[period.key] || period.label}
                    staggerIndex={index}
                    animate={animateKpiNumbers}
                    cardClassName={isActive ? 'kpi-banner-card--accent-neon-top' : ''}
                  />
                </div>
              );
            })}
          </div>

          <SecFilingInsightsSummary
            period={activePeriod}
            summary={insights?.summary}
            periodTableRows={insights?.rows || []}
            loading={loading}
            animateEnter={!insightsEntered && Boolean(insights)}
            periodSwitchVersion={periodSwitchVersion}
            filters={filters}
            tableCompanyFilter={tableCompanyFilter}
            onTableCompanyFilterChange={setTableCompanyFilter}
            onViewFilingsCompany={setFilterCompany}
            tablePage={tablePage}
            onTablePageChange={setTablePage}
            subjectCompany={meta?.subjectCompany || ''}
          />
        </div>
      </div>

      <SecFilingFiltersDrawer
        isOpen={showTableFilters}
        onClose={() => setShowTableFilters(false)}
        filters={filters}
        setCurrentPage={setTablePage}
      />
    </>
  );
}
